import { DangerAlert } from "components/common/Alerts";
import { Breadcrumb, BreadcrumbLink } from "components/common/Breadcrumb";
import ContentLoading from "components/common/ContentLoading";
import { UnitPriceType } from "enums/UnitPriceType";
import QuoteHistory from "interfaces/QuoteHistory";
import * as moment from "moment";
import * as numeral from "numeral";
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import apifetch from "utils/apifetch";
import downloadBlob from "utils/downloadBlob";
import extractErrorMessage from "utils/extractErrorMessage";
import "./View.css";

interface RouteParams {
    id: number;
}

interface State {
    isLoading: boolean;
    errorMessage?: string;
    data: QuoteHistory;
}

export default class View extends React.Component<RouteComponentProps<RouteParams>, State> {
    private readonly baseUrl: string;

    constructor(props: RouteComponentProps<RouteParams>) {
        super(props);

        this.state = {
            isLoading: true,
            errorMessage: undefined,
            data: {} as any, // Will be assigned to when componentDidMount is called.
        };

        this.baseUrl = document.getElementsByTagName('base')[0].getAttribute('href')!;

        this.viewPdfForCustomer = this.viewPdfForCustomer.bind(this);
    }

    public render() {
        if (this.state.isLoading) {
            return (
                <div className="container">
                    <ContentLoading />
                </div>
            );
        }

        let quoteId = this.state.data.id.toString();
        if (this.state.data.isMaster) {
            quoteId += "M";
        }
        if (this.state.data.revisionDate) {
            quoteId += ", rev " + moment(this.state.data.revisionDate).format("l");
        }
        if (this.state.data.isDraft) {
            quoteId += " (Draft)";
        }

        return <div className="container">
            <div className="animated fadeIn">
                <Breadcrumb>
                    <BreadcrumbLink to="/">Home</BreadcrumbLink>
                    <BreadcrumbLink to="/quotes">Quotes</BreadcrumbLink>
                    <BreadcrumbLink isActive={true}>{this.state.data.jobName}</BreadcrumbLink>
                </Breadcrumb>
                {this.state.errorMessage && (
                    <DangerAlert name="dangerAlert">{this.state.errorMessage}</DangerAlert>
                )}
                <div className="box-wrap">
                    <h2 className="card__title">
                        Overview
                    </h2>
                    <div className="box">
                        <div className="row">
                            <div className="field-group col-sm-6">
                                <span className="field-label">Project Name</span>
                                <span className="field-item">{this.state.data.jobName}</span>
                            </div>
                            <div className="field-group col-sm-6">
                                <span className="field-label">City</span>
                                <span className="field-item">{this.state.data.jobCity}</span>
                            </div>
                        </div>
                        <div className="row">
                            <div className="field-group col-sm-6">
                                <span className="field-label">Quote ID</span>
                                <span className="field-item">{quoteId}</span>
                            </div>
                            <div className="field-group col-sm-6">
                                <span className="field-label">PO #</span>
                                <span className="field-item">{this.state.data.purchaseOrderNumber}</span>
                            </div>
                        </div>
                        <div className="row">
                            <div className="field-group col-sm-6">
                                <span className="field-label">Date</span>
                                <span className="field-item">{this.state.data.date && moment(this.state.data.date).format("LL")}</span>
                            </div>
                            <div className="field-group col-sm-6">
                                <span className="field-label">Expiry Date</span>
                                <span className="field-item">{this.state.data.expirationDate && moment(this.state.data.expirationDate).format("LL")}</span>
                            </div>
                        </div>
                        <div className="row">
                            <div className="field-group col-sm-6">
                                <span className="field-label">Bid Date</span>
                                <span className="field-item">{this.state.data.jobBidDate && moment(this.state.data.jobBidDate).format("LL")}</span>
                            </div>
                            <div className="field-group col-sm-6">
                                <span className="field-label">Salesperson</span>
                                <span className="field-item">{this.state.data.salespersonName}</span>
                            </div>
                        </div>
                        <div className="row">
                            <div className="field-group col-sm-6 quote-status">
                                <span className="field-label">Status</span>
                                <span className="field-item">{this.state.data.quoteStatusName}</span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="card card--datatables pb-4 mb-0">
                    <div className="card-block">
                        <h2 className="card__title">Customers</h2>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Customer</th>
                                    <th>ATTN</th>
                                    <th style={{ width: "1%" }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.data.customers.map((item, index) =>
                                    <tr key={item.customerId}>
                                        <td style={{ verticalAlign: "middle" }}><Link to={"/customers/" + item.customerId}>{item.customerName} ({item.customerApiType === 1 ? "BC" : "BQS"})</Link></td>
                                        <td style={{ verticalAlign: "middle" }}>{item.customerContactName}</td>
                                        <td>
                                            <a href="#" onClick={event => this.viewPdfForCustomer(event, item.customerId)} className="btn btn-primary" style={{ display: "inline-block" }}>View PDF</a>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="card card--datatables pb-4 mb-0">
                    <div className="card-block">
                        <h2 className="card__title">Material Details</h2>
                        {this.state.data.productGroups.map((item, index) =>
                            <div key={item.name}>
                                <h5>{item.name}</h5>
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th style={{ width: "10%" }}>Quantity</th>
                                            <th style={{ width: "10%" }}>UM</th>
                                            <th style={{ width: "30%" }}>Item</th>
                                            <th style={{ width: "30%" }}>Notes</th>
                                            <th style={{ width: "10%" }}>Price</th>
                                            <th style={{ width: "10%" }}>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {item.products.map((item, index) =>
                                            <tr key={item.productId}>
                                                <td>{item.unitPriceTypeId !== UnitPriceType.Note && item.quantity}</td>
                                                <td>{item.unitMeasurement}</td>
                                                <td>{!item.productId && item.notes && item.unitPriceTypeId !== UnitPriceType.Note ? item.notes : item.productName}</td>
                                                <td>{(item.productId || item.unitPriceTypeId === UnitPriceType.Note) && item.notes}</td>
                                                <td className="text-right">{item.unitPriceTypeId ? item.unitPriceTypeName : numeral(item.price).format("$0,0.00")}</td>
                                                <td className="text-right">{!item.unitPriceTypeId && numeral(item.amount).format("$0,0.00")}</td>
                                            </tr>
                                        )}
                                        <tr className="sub-total">
                                            <td colSpan={4}></td>
                                            <td>Subtotal</td>
                                            <td className="text-right">{numeral(item.subTotal).format("$0,0.00")}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
                <div className="grand-total-box">
                    {this.state.data.salesTaxRate !== null &&
                        <div className="sales-tax2">
                            <span>Sales Tax ({numeral(this.state.data.salesTaxRate!).format("0.[0000]%")})</span>
                            <span>{numeral(this.state.data.salesTax!).format("$0,0.00")}</span>
                        </div>
                    }
                    <div className="grand-total2">
                        <span>Grand Total</span>
                        <span>{numeral(this.state.data.price).format("$0,0.00")}</span>
                    </div>
                </div>
                <div className="button-actions text-center" style={{ marginBottom: "40px" }}>
                    <Link to={"/quotes/" + this.state.data.quoteId} className="btn">Back to Quote</Link>
                </div>
            </div>
        </div>;
    }

    public async componentDidMount() {
        if (this.state.isLoading) {
            let response = await apifetch("quotes/history/" + this.props.match.params.id, undefined, true);
            if (response.ok) {
                let data = await response.json() as QuoteHistory;
                this.setState({
                    isLoading: false,
                    data: data,
                });

                document.title = `Viewing Quote History for ${this.state.data.jobName} - Quotes - ${SITE_NAME}`;
            } else {
                const errorText = await extractErrorMessage(response);
                this.setState({
                    isLoading: false,
                    errorMessage: errorText,
                });
            }
        }
    }

    private async viewPdfForCustomer(event: React.SyntheticEvent<HTMLAnchorElement>, customerId: number) {
        event.preventDefault();

        let response = await apifetch(`quotes/history/pdf/${this.props.match.params.id}/customer/${customerId}`, undefined, true);
        if (response.ok) {
            let blob = await response.blob();

            downloadBlob(blob, `Quote ${this.state.data.quoteId}.pdf`, {
                type: "application/pdf",
            });
        } else {
            const errorText = await extractErrorMessage(response);
            this.setState({ errorMessage: errorText });
        }
    }
}



// WEBPACK FOOTER //
// ./ClientApp/components/quotes/ViewHistory.tsx